import { motion } from "framer-motion";
import {
  AlertTriangle,
  Camera,
  BrainCircuit,
  Users,
  FolderKanban,
  CheckCircle2,
} from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

const NODES = [
  { icon: AlertTriangle, label: "Incident" },
  { icon: Camera, label: "Evidence Collection" },
  { icon: BrainCircuit, label: "AI Analysis" },
  { icon: Users, label: "Professional Coordination" },
  { icon: FolderKanban, label: "Case Management" },
  { icon: CheckCircle2, label: "Resolution" },
];

export function PlatformFlow() {
  return (
    <section
      id="flow"
      className="relative mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-32"
      data-testid="platform-flow-section"
    >
      <Reveal>
        <p className="font-mono text-xs text-white/40">03 // SYSTEM FLOW</p>
      </Reveal>
      <Reveal delay={0.05}>
        <h2 className="font-display mt-6 max-w-2xl text-4xl font-light tracking-tight text-white sm:text-5xl">
          One connected workflow, from incident to resolution.
        </h2>
      </Reveal>

      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-60px" }}
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.14 } } }}
        className="mt-16 flex flex-col gap-4 lg:flex-row lg:items-stretch lg:gap-2"
      >
        {NODES.map((n, i) => (
          <div key={n.label} className="flex items-center lg:flex-1 lg:flex-col">
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 20 },
                show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
              }}
              className="group relative flex w-full flex-1 flex-col items-center rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-center transition-colors duration-300 hover:border-white/25"
              data-testid={`flow-node-${i}`}
            >
              <div
                className="flex h-14 w-14 items-center justify-center rounded-full border border-white/10 animate-pulse-node"
                style={{
                  animationDelay: `${i * 0.4}s`,
                  boxShadow: "0 0 22px var(--accent-glow)",
                }}
              >
                <n.icon className="h-6 w-6" style={{ color: "var(--accent)" }} />
              </div>
              <span className="font-mono mt-4 text-[10px] tracking-[0.15em] text-white/40">
                {String(i + 1).padStart(2, "0")}
              </span>
              <p className="font-display mt-1 text-sm font-medium text-white">
                {n.label}
              </p>
            </motion.div>

            {i < NODES.length - 1 && (
              <motion.div
                variants={{
                  hidden: { opacity: 0 },
                  show: { opacity: 1, transition: { duration: 0.4 } },
                }}
                className="flex items-center justify-center px-2 py-3 lg:py-0"
                aria-hidden="true"
              >
                <span className="hidden h-px w-full flex-1 bg-gradient-to-r from-white/20 to-white/5 lg:block lg:w-6" />
                <span
                  className="text-lg lg:hidden"
                  style={{ color: "var(--accent)" }}
                >
                  ↓
                </span>
                <span
                  className="hidden lg:inline"
                  style={{ color: "var(--accent)" }}
                >
                  →
                </span>
              </motion.div>
            )}
          </div>
        ))}
      </motion.div>
    </section>
  );
}
